/**
 * AdminEntityList - Generic entity list component
 *
 * Features:
 * - Search bar at top (debounced, server-side)
 * - Sortable columns
 * - Server-side pagination
 * - Inline editing for editable columns
 * - Row click to navigate to detail view
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../../services/admin-api';
import { getTableColumns, getDefaultSortColumn } from '../config/table-columns';

export function AdminEntityList() {
  const { table } = useParams<{ table: string }>();
  const navigate = useNavigate();

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(table ? getDefaultSortColumn(table) : 'created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [editingCell, setEditingCell] = useState<{ rowId: string; column: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  const searchTimeout = useRef<NodeJS.Timeout>();
  const limit = 50;

  const columns = table ? getTableColumns(table) : [];

  useEffect(() => {
    if (table) {
      loadData();
    }
  }, [table, sortColumn, sortOrder, page]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      if (table) {
        setPage(0);
        loadData();
      }
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery]);

  const loadData = async () => {
    if (!table) return;

    try {
      setLoading(true);
      const data = await adminApi.listEntities(table, {
        limit,
        offset: page * limit,
        sort: sortColumn,
        order: sortOrder,
        search: searchQuery || undefined,
      });

      setRows(data.rows);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load entities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const handleCellEdit = async (rowId: string, column: string, value: any) => {
    if (!table) return;

    try {
      await adminApi.updateEntity(table, rowId, { [column]: value });
      setEditingCell(null);
      await loadData();
    } catch (error: any) {
      console.error('Failed to update entity:', error);
      alert(`Failed to update: ${error.message}`);
      setEditingCell(null);
    }
  };

  const startEdit = (rowId: string, column: string, currentValue: any) => {
    setEditingCell({ rowId, column });
    setEditValue(currentValue?.toString() || '');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const getPrimaryKey = (row: any): string => {
    // Handle different primary key types
    if (table === 'users') return row.cognito_id;
    return row.id;
  };

  const formatCellValue = (value: any, type: string): string => {
    if (value === null || value === undefined) return '-';

    switch (type) {
      case 'date':
        // Date-only values (from DATE columns): display without timezone shift
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T00:00:00/.test(value)) {
          const [year, month, day] = value.split('T')[0].split('-').map(Number);
          return new Date(year, month - 1, day).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
        }
        return new Date(value).toLocaleString();
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'json':
        return JSON.stringify(value);
      case 'uuid':
        return value.substring(0, 8) + '...';
      default:
        return value.toString();
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (!table) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">Invalid table</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 capitalize">{table.replace(/_/g, ' ')}</h1>
        <p className="text-gray-500 mt-2">
          {total.toLocaleString()} {total === 1 ? 'record' : 'records'}
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full max-w-md px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg
            className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No records found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        onClick={() => column.sortable && handleSort(column.key)}
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                          column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                        }`}
                        style={{ width: column.width }}
                      >
                        <div className="flex items-center">
                          {column.label}
                          {column.sortable && sortColumn === column.key && (
                            <svg
                              className={`ml-1 w-4 h-4 ${sortOrder === 'asc' ? 'transform rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          )}
                        </div>
                      </th>
                    ))}
                    {table === 'leagues' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((row) => {
                    const rowId = getPrimaryKey(row);
                    return (
                      <tr
                        key={rowId}
                        onClick={() => navigate(`/admin/entities/${table}/${rowId}`)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        {columns.map((column) => {
                          const isEditing =
                            editingCell?.rowId === rowId && editingCell?.column === column.key;
                          const cellValue = row[column.key];

                          return (
                            <td
                              key={column.key}
                              className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                              onClick={(e) => {
                                if (column.editable) {
                                  e.stopPropagation();
                                  startEdit(rowId, column.key, cellValue);
                                }
                              }}
                            >
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleCellEdit(rowId, column.key, editValue);
                                    } else if (e.key === 'Escape') {
                                      cancelEdit();
                                    }
                                  }}
                                  onBlur={cancelEdit}
                                  autoFocus
                                  className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <div
                                  className={
                                    column.editable ? 'hover:bg-gray-100 rounded px-2 py-1' : ''
                                  }
                                >
                                  {formatCellValue(cellValue, column.type)}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        {table === 'leagues' && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/leagues/${row.id}`);
                              }}
                              className="px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700"
                            >
                              View League
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of{' '}
                {total.toLocaleString()} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
